import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import introJs from 'intro.js';
import { FeatureSettingsService } from '@app/services/feature-settings.service';

const ORIENTATION_DONE_KEY      = 'studema_orientation_done';
const ORIENTATION_AUTOSTART_KEY = 'studema_orientation_autostart';

@Injectable({ providedIn: 'root' })
export class OrientationService {
    private introInstance: any = null;
    /** Phase currently on screen; step texts are built once, so a language switch restarts it. */
    private activePhase: 1 | 2 | null = null;
    private restarting = false;

    constructor(
        private router: Router,
        private translate: TranslateService,
        private featureSettings: FeatureSettingsService,
    ) {
        this.translate.onLangChange.subscribe(() => this.restartForLanguage());
    }

    private async restartForLanguage(): Promise<void> {
        const phase = this.activePhase;
        if (!phase || !this.introInstance) { return; }
        this.restarting = true;
        await this.introInstance.exit(true);
        this.restarting = false;
        if (phase === 1) {
            this.startPhase1_Home();
        } else {
            this.startPhase2_CourseDetail();
        }
    }

    // ─── Feature flag ──────────────────────────────────────────────────────────

    /** Returns true when the orientation tour feature is enabled for this company. */
    isEnabled(): boolean {
        return this.featureSettings.isFeatureEnabled('feature_orientation_tour');
    }

    // ─── Auto-start management ─────────────────────────────────────────────────

    /** Called after the onboarding wizard completes — sets a flag so the dashboard auto-starts the tour. */
    scheduleAutoStart(): void {
        if (!this.isEnabled()) { return; }
        localStorage.setItem(ORIENTATION_AUTOSTART_KEY, 'true');
    }

    /** Returns true if the tour should auto-start (first time after onboarding). */
    shouldAutoStart(): boolean {
        if (!this.isEnabled()) { return false; }
        return localStorage.getItem(ORIENTATION_AUTOSTART_KEY) === 'true';
    }

    /** Check whether orientation has ever been completed. */
    isCompleted(): boolean {
        return localStorage.getItem(ORIENTATION_DONE_KEY) === 'true';
    }

    /** Mark the orientation as completed. */
    markComplete(): void {
        localStorage.setItem(ORIENTATION_DONE_KEY, 'true');
        localStorage.removeItem(ORIENTATION_AUTOSTART_KEY);
    }

    /** Reset so the tour can run again. */
    reset(): void {
        localStorage.removeItem(ORIENTATION_DONE_KEY);
        localStorage.removeItem(ORIENTATION_AUTOSTART_KEY);
    }

    // ─── Public API ─────────────────────────────────────────────────────────────

    /**
     * Start the tour from the user menu (always starts from Phase 1 on /home).
     * This is the student-only tour — no sidebar steps.
     */
    startTourFromMenu(): void {
        if (!this.isEnabled()) { return; }
        this.reset();
        if (this.router.url.startsWith('/home')) {
            this.startPhase1_Home();
        } else {
            this.router.navigateByUrl('/home').then(() => {
                this.startPhase1_Home();
            });
        }
    }

    // ─── Phase 1: Dashboard / Course List ──────────────────────────────────────

    /**
     * Student orientation tour — Phase 1 on the dashboard.
     *
     * NOTE: Students have NO sidebar navigation. The <fuse-vertical-navigation>
     * is wrapped in *ngIf="showVerticalNavigation" which is false for role=5.
     * nav-sidebar is intentionally excluded from this tour.
     *
     * Step order (top-bar layout):
     *   0  Welcome overlay (no element)
     *   1  course-list
     *   2  course-card-first
     *   → auto-navigates into the first enrolled course → Phase 2
     */
    async startPhase1_Home(): Promise<void> {
        // Clear the autostart flag immediately so it does not fire again on the next render
        localStorage.removeItem(ORIENTATION_AUTOSTART_KEY);

        // Wait for the course list, then briefly for the first card (the enrollments API may
        // still be loading). Students without an open course get a short tour ending in "Done".
        await this.waitForElement('[data-tour="course-list"]', 3000);
        await this.waitForElement('[data-tour="course-card-first"]', 3000);
        const hasCourse = this.isVisible('[data-tour="course-card-first"]');

        const steps = [
            // Step 0 — full-overlay welcome, no element
            {
                title: this.t('tour.welcome.title', 'Welcome to Studema'),
                intro: this.t(
                    'tour.welcome.desc',
                    'This short tour shows you where everything is. Use <strong>Next</strong> and <strong>Back</strong> to navigate, or <strong>Skip</strong> to close it. You can replay this tour anytime.',
                ),
            },
            // Step 1 — course list
            {
                element:  '[data-tour="course-list"]',
                title:    this.t('tour.courseList.title', 'Your Courses'),
                intro:    this.t(
                    'tour.courseList.desc',
                    'All courses you are enrolled in appear here. Each card shows the course name and your current progress.',
                ),
                position: 'bottom',
            },
            // Step 2 — first course card
            {
                element:  '[data-tour="course-card-first"]',
                title:    this.t('tour.courseCard.title', 'Open a Course'),
                intro:    this.t(
                    'tour.courseCard.desc',
                    'Click any course card to open it and start working through its steps.',
                ),
                position: 'bottom',
            },
        ];

        // Steps with an element are only included when the element is actually visible.
        const validSteps = steps.filter(
            (s: any) => !s.element || this.isVisible(s.element),
        );

        this.introInstance = introJs();
        this.introInstance.setOptions({
            steps:             validSteps,
            showProgress:      true,
            showBullets:       true,
            exitOnOverlayClick: false,
            showStepNumbers:   false,
            doneLabel:         hasCourse
                ? this.t('tour.btn.next', 'Next →')
                : this.t('tour.btn.done', 'Done ✓'),
            nextLabel:         this.t('tour.btn.next', 'Next →'),
            prevLabel:         this.t('tour.btn.prev', '← Back'),
            skipLabel:         this.t('tour.btn.skip', 'Skip'),
            tooltipClass:      'studema-tour-tooltip',
            highlightClass:    'studema-tour-highlight',
        });

        // Set tooltip on the × skip button so hover reveals the translated label
        const skipLabel1 = this.t('tour.btn.skip', 'Skip');
        const setSkipTitle1 = () => {
            const btn = document.querySelector('.studema-tour-tooltip .introjs-skipbutton') as HTMLElement | null;
            if (btn) { btn.title = skipLabel1; }
        };
        this.introInstance.onafterchange(setSkipTitle1);
        this.introInstance.onstart(setSkipTitle1);

        this.introInstance.oncomplete(() => {
            this.activePhase = null;
            this.navigateToCourseAndStartPhase2();
        });

        this.introInstance.onexit(() => {
            if (this.restarting) { return; }
            this.activePhase = null;
            localStorage.removeItem(ORIENTATION_AUTOSTART_KEY);
        });

        this.activePhase = 1;
        this.introInstance.start();
    }

    // ─── Phase 2: Course Detail Page ───────────────────────────────────────────

    /**
     * Student orientation tour — Phase 2 inside a course detail page.
     *
     * Step order:
     *   1  course-header
     *   2  progress-indicator   (with full colour legend incl. orange, brown, red)
     *   3  checksheet
     *   4  step-content
     *   5  step-done-btn        ← MOST IMPORTANT step
     *   6  messages overlay     ← floating, explains the phuture-comments / Show Messages block
     *   7  chat-btn             ← chat BEFORE help
     *   8  clearword-btn        (skipped if feature off)
     *   9  support-btn          ← help AFTER chat
     *  10  user-menu            ← opens the mega menu
     *  11  Finish overlay
     */
    startPhase2_CourseDetail(): void {
        const steps = [
            // ── Course header ──
            {
                element:  '[data-tour="course-header"]',
                title:    this.t('tour.courseHeader.title', 'Course Overview'),
                intro:    this.t(
                    'tour.courseHeader.desc',
                    'This header shows the course title and a back arrow to return to your course list.',
                ),
                position: 'bottom',
            },

            // ── Progress dots — full colour legend ──
            {
                element:  '[data-tour="progress-indicator"]',
                title:    this.t('tour.progress.title', 'Your Progress'),
                intro:    this.t(
                    'tour.progress.desc',
                    'Each dot represents one step in your course. Here is what each colour means:<br><br>' +
                    '⬛ <strong>Grey</strong> — not started yet<br>' +
                    '🟢 <strong>Green</strong> — completed<br>' +
                    '🟠 <strong>Orange</strong> — submitted and waiting for your supervisor to review<br>' +
                    '🟤 <strong>Brown</strong> — your supervisor has reviewed and is waiting for your answer<br>' +
                    '🔴 <strong>Red</strong> — rejected; you need to redo this step<br><br>' +
                    'Your current active step is highlighted.',
                ),
                position: 'bottom',
            },

            // ── Step list ──
            {
                element:  '[data-tour="checksheet"]',
                title:    this.t('tour.checksheet.title', 'Step List'),
                intro:    this.t(
                    'tour.checksheet.desc',
                    'This panel lists all learning steps in order. Click any step to open it. Completed steps are ticked.',
                ),
                position: 'right',
            },

            // ── Step content ──
            {
                element:  '[data-tour="step-content"]',
                title:    this.t('tour.stepContent.title', 'Step Content'),
                intro:    this.t(
                    'tour.stepContent.desc',
                    'The main learning area. Read the instructions, watch videos, or complete the exercise shown here.',
                ),
                position: 'left',
            },

            // ── DONE button — the most important step ──
            {
                element:  '[data-tour="step-done-btn"]',
                title:    this.t('tour.stepDone.title', '⚠️ This is the Most Important Button'),
                intro:    this.t(
                    'tour.stepDone.desc',
                    '<strong>When you have finished a step, you MUST click the <span style="color:#4f46e5">DONE</span> button.</strong><br><br>' +
                    'This saves your progress, notifies your supervisor, and moves you to the next step. ' +
                    'Without clicking DONE your progress will not be recorded and your supervisor will not know you are ready.',
                ),
                position: 'top',
            },

            // ── Messages / phuture-comments — floating overlay ──
            {
                // No element — always shows as a centred overlay
                title: this.t('tour.messages.title', 'Messages with Your Supervisor'),
                intro: this.t(
                    'tour.messages.desc',
                    'Below each step you will find a <strong>Show Messages</strong> section. ' +
                    'You can use it at any time to send a message to your supervisor or see their feedback on your work. ' +
                    'Your supervisor is notified when you write and you are notified when they reply.',
                ),
            },

            // ── Chat button — FIRST ──
            {
                element:  '[data-tour="chat-btn"]',
                title:    this.t('tour.chat.title', 'Ask Your Supervisor'),
                intro:    this.t(
                    'tour.chat.desc',
                    'Stuck on something? Click here to open a direct chat with your supervisor. They will be notified and reply here.',
                ),
                position: 'bottom',
            },

            // ── Clear Word / Dictionary ──
            {
                element:  '[data-tour="clearword-btn"]',
                title:    this.t('tour.clearword.title', 'Clear Word — Dictionary'),
                intro:    this.t(
                    'tour.clearword.desc',
                    'Encountered an unfamiliar word? Click here to look it up in the built-in dictionary without leaving your course.',
                ),
                position: 'bottom',
            },

            // ── Help / Support — AFTER chat ──
            {
                element:  '[data-tour="support-btn"]',
                title:    this.t('tour.support.title', 'Help & Support'),
                intro:    this.t(
                    'tour.support.desc',
                    'For technical issues or general questions, click here to reach the support team.',
                ),
                position: 'bottom',
            },

            // ── User account menu — opens the mega menu ──
            {
                element:  '[data-tour="user-menu"]',
                title:    this.t('tour.settings.title', 'Your Account Menu'),
                intro:    this.t(
                    'tour.settings.desc',
                    'This is your personal menu. It gives you quick access to <strong>all functions you have at your disposal at all times</strong>: ' +
                    'your study schedule, study path, settings, help, orientation tour, and more.',
                ),
                position: 'left',
            },

            // ── Finish overlay ──
            {
                title: this.t('tour.finish.title', 'You are all set!'),
                intro: this.t(
                    'tour.finish.desc',
                    'You now know the most important parts of the platform. ' +
                    'Enjoy every step of your learning journey — you can replay this tour at any time from your account menu.',
                ),
            },
        ];

        const validSteps = steps.filter(
            (s: any) => !s.element || this.isVisible(s.element),
        );

        if (validSteps.length === 0) {
            this.markComplete();
            return;
        }

        this.introInstance = introJs();
        this.introInstance.setOptions({
            steps:              validSteps,
            showProgress:       true,
            showBullets:        true,
            exitOnOverlayClick: false,
            showStepNumbers:    false,
            doneLabel:          this.t('tour.btn.start', 'Start! →'),
            nextLabel:          this.t('tour.btn.next', 'Next →'),
            prevLabel:          this.t('tour.btn.prev', '← Back'),
            skipLabel:          this.t('tour.btn.skip', 'Skip'),
            tooltipClass:       'studema-tour-tooltip',
            highlightClass:     'studema-tour-highlight',
        });

        // Open the mega menu on the user-menu step and close it on any other step.
        // Decided from the panel's presence, not a toggle: clicking Next/Back already closes
        // the menu via UserComponent's document:click handler, keyboard navigation does not.
        this.introInstance.onbeforechange((targetElement: HTMLElement) => {
            const onMenuStep = targetElement === document.querySelector('[data-tour="user-menu"]');
            if (onMenuStep !== this.isUserMenuOpen()) {
                this.toggleUserMenu();
            }
        });

        // Set tooltip on the × skip button so hover reveals the translated label
        const skipLabel2 = this.t('tour.btn.skip', 'Skip');
        const setSkipTitle2 = () => {
            const btn = document.querySelector('.studema-tour-tooltip .introjs-skipbutton') as HTMLElement | null;
            if (btn) { btn.title = skipLabel2; }
        };
        this.introInstance.onafterchange(setSkipTitle2);
        this.introInstance.onstart(setSkipTitle2);

        this.introInstance.oncomplete(() => {
            this.activePhase = null;
            this.markComplete();
        });

        this.introInstance.onexit(() => {
            if (this.isUserMenuOpen()) { this.toggleUserMenu(); }
            if (this.restarting) { return; }
            this.activePhase = null;
            this.markComplete();
        });

        this.activePhase = 2;
        this.introInstance.start();
    }

    // ─── Helpers ────────────────────────────────────────────────────────────────

    /**
     * Navigate into the first enrolled course, then start Phase 2.
     *
     * Waits for both course-header AND step-done-btn to appear in the DOM
     * before running the step filter — this fixes the bug where the DONE button
     * was skipped because the secondary router outlet hadn't rendered yet.
     */
    private async navigateToCourseAndStartPhase2(): Promise<void> {
        const firstCard = document.querySelector('[data-tour="course-card-first"]') as HTMLElement | null;
        const scid      = firstCard?.getAttribute('data-tour-scid');

        if (scid) {
            await this.router.navigate(['/academy', +scid]);
            // Wait for the course header (routing is complete)
            await this.waitForElement('[data-tour="course-header"]', 5000);
            // Also wait for the step content (secondary outlet) to render
            await this.waitForElement('[data-tour="step-done-btn"]', 5000);
            this.startPhase2_CourseDetail();
        } else {
            // No enrolled course found — graceful exit
            this.markComplete();
        }
    }

    /**
     * Polls the DOM until `selector` is present or `timeoutMs` elapses.
     * Returns true if found, false on timeout.
     */
    private waitForElement(selector: string, timeoutMs = 3000): Promise<boolean> {
        return new Promise(resolve => {
            if (document.querySelector(selector)) {
                resolve(true);
                return;
            }
            const interval = setInterval(() => {
                if (document.querySelector(selector)) {
                    clearInterval(interval);
                    resolve(true);
                }
            }, 200);
            setTimeout(() => {
                clearInterval(interval);
                resolve(false);
            }, timeoutMs);
        });
    }

    private isUserMenuOpen(): boolean {
        return !!document.querySelector('[data-tour="user-menu-panel"]');
    }

    /** Clicks the menu's own trigger button; the <user> host element has no click handler. */
    private toggleUserMenu(): void {
        (document.querySelector('[data-tour="user-menu"] button') as HTMLElement | null)?.click();
    }

    /** True when `selector` matches an element that is rendered (not display:none / detached). */
    private isVisible(selector: string): boolean {
        const el = document.querySelector(selector) as HTMLElement | null;
        return !!el && el.getClientRects().length > 0;
    }

    /** Translate helper with fallback to the provided English string. */
    private t(key: string, fallback: string): string {
        const translated = this.translate.instant(key);
        return translated === key ? fallback : translated;
    }
}
